import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Animated flow diagram: BUSINESS → PRESENCE → TRUST → ENQUIRIES → GROWTH
// A bespoke architectural plan-view network in pure SVG.

const NODES = [
    { id: "biz", label: "Business", n: "00", x: 90, y: 410 },
    { id: "pres", label: "Presence", n: "01", x: 260, y: 200 },
    { id: "trust", label: "Trust", n: "02", x: 430, y: 410 },
    { id: "enq", label: "Enquiries", n: "03", x: 600, y: 220 },
    { id: "grow", label: "Growth", n: "04", x: 770, y: 410 },
];

const PATHS = [
    "M 90 410 C 160 410, 200 260, 260 200",
    "M 260 200 C 330 200, 360 360, 430 410",
    "M 430 410 C 500 410, 530 280, 600 220",
    "M 600 220 C 670 220, 700 380, 770 410",
];

export const HeroFlow = () => {
    return (
        <div
            data-testid="hero-flow"
            className="relative w-full aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[#0f0f0f] text-white"
        >
            {/* Architectural grid */}
            <div
                className="absolute inset-0 opacity-[0.18] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.18) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }}
            />
            <div
                className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
                    backgroundSize: "12px 12px",
                }}
            />

            {/* Floating index marks */}
            <div className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center gap-3 z-10">
                <span className="w-2 h-2 rounded-full bg-white inline-block" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-white/70">
                    Pixemance · System Schematic
                </span>
            </div>
            <div className="absolute top-6 right-6 md:top-8 md:right-8 text-right z-10">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/50">
                    Plate № 01
                </p>
                <p className="font-serif italic text-white/70 text-sm mt-1">
                    Business → Growth
                </p>
            </div>

            <svg
                viewBox="0 0 860 700"
                preserveAspectRatio="xMidYMid meet"
                className="absolute inset-0 w-full h-full"
                aria-hidden
            >
                {/* Reference rulers */}
                <motion.line
                    x1="0" y1="120" x2="860" y2="120"
                    stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="3 6"
                    initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                    transition={{ duration: 1.6, delay: 0.2, ease: EASE }}
                />
                <motion.line
                    x1="0" y1="540" x2="860" y2="540"
                    stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="3 6"
                    initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                    transition={{ duration: 1.6, delay: 0.4, ease: EASE }}
                />

                {/* Connecting flow paths */}
                {PATHS.map((d, i) => (
                    <motion.path
                        key={i}
                        d={d}
                        fill="none"
                        stroke="white"
                        strokeWidth="1.25"
                        strokeLinecap="round"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{
                            duration: 1.4,
                            delay: 0.6 + i * 0.35,
                            ease: EASE,
                        }}
                    />
                ))}

                {/* Nodes */}
                {NODES.map((node, i) => (
                    <motion.g
                        key={node.id}
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.4 + i * 0.18, ease: EASE }}
                    >
                        {/* Outer ring */}
                        <circle
                            cx={node.x}
                            cy={node.y}
                            r="36"
                            fill="#0f0f0f"
                            stroke="white"
                            strokeWidth="1"
                        />
                        <circle
                            cx={node.x}
                            cy={node.y}
                            r="44"
                            fill="none"
                            stroke="rgba(255,255,255,0.18)"
                            strokeWidth="1"
                            strokeDasharray="2 5"
                        />
                        <circle cx={node.x} cy={node.y} r="4" fill="white" />
                        {/* Index */}
                        <text
                            x={node.x}
                            y={node.y - 56}
                            textAnchor="middle"
                            className="fill-white/50"
                            style={{
                                fontFamily: "Satoshi, sans-serif",
                                fontSize: "10px",
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                            }}
                        >
                            ◇ {node.n}
                        </text>
                        {/* Label */}
                        <text
                            x={node.x}
                            y={node.y + 70}
                            textAnchor="middle"
                            className="fill-white"
                            style={{
                                fontFamily: "Cormorant Garamond, serif",
                                fontSize: "22px",
                                fontStyle: "italic",
                            }}
                        >
                            {node.label}
                        </text>
                    </motion.g>
                ))}

                {/* Decorative corner brackets */}
                <g stroke="white" strokeWidth="1" fill="none">
                    <motion.path
                        d="M 30 30 L 30 70 M 30 30 L 70 30"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8, delay: 1.6, ease: EASE }}
                    />
                    <motion.path
                        d="M 830 670 L 830 630 M 830 670 L 790 670"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8, delay: 1.8, ease: EASE }}
                    />
                </g>
            </svg>

            {/* Floating index card */}
            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 2.0, ease: EASE }}
                className="absolute bottom-6 left-6 md:bottom-8 md:left-8 bg-white text-[#111111] px-5 py-4 max-w-[260px]"
            >
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                    Reading the chart
                </p>
                <p className="mt-2 font-serif text-lg leading-snug">
                    Five quiet forces — engineered to compound into measurable
                    enquiries.
                </p>
            </motion.div>

            {/* Bottom metric strip */}
            <div className="absolute bottom-0 left-0 right-0 border-t border-white/15 grid grid-cols-3 text-[11px] tracking-[0.2em] uppercase text-white/60">
                <div className="px-4 py-3 border-r border-white/15">
                    +1.6× enquiries
                </div>
                <div className="px-4 py-3 border-r border-white/15">
                    90 days
                </div>
                <div className="px-4 py-3 text-right">live</div>
            </div>
        </div>
    );
};
